import { motion } from "framer-motion";
import { Scale, X, Printer, ExternalLink, AlertTriangle } from "lucide-react";

const blankLine = "border-b border-slate-400 min-w-0 flex-1 h-7 bg-transparent outline-none text-sm";

const AGREEMENT_RULES = [
  "የሁለቱንም ወገኖች ሙሉ ስምና መታወቂያ ይመዝግቡ።",
  "የስምምነቱን ዓላማ በግልጽ ይጻፉ።",
  "የእያንዳንዱን ወገን ግዴታ ይወስኑ።",
  "የገንዘብ መጠንና የክፍያ ሁኔታ ይግለጹ።",
  "የሚፈጸምበትን ቀንና ጊዜ ይግለጹ።",
  "ስምምነቱ ካልተፈጸመ የሚኖረውን ኃላፊነት ይጻፉ።",
  "የጉዳትና የኪሳራ ኃላፊነትን ይወስኑ።",
  "ስምምነቱን ለማቋረጥ የሚያስፈልገውን ሂደት ይግለጹ።",
  "ክርክር ቢነሳ እንዴት እንደሚፈታ ይጻፉ።",
  "ሁለቱም ወገኖች ፈርመው በሕጉ መሠረት ማረጋገጥና መመዝገብ ካስፈለገ ያከናውኑ።"
];

const CONTRACT_REQUIREMENTS = [
  "ውሉን የሚፈጽሙ ሰዎች በሕግ የመዋዋል ብቃት ሊኖራቸው ይገባል።",
  "ሁለቱም ወገኖች በነፃ ፈቃዳቸው መስማማት አለባቸው።",
  "የሚደረገው ስምምነት ግልጽ፣ ሊፈጸም የሚችልና ሕጋዊ መሆን አለበት።",
  "ሕጉ የተለየ የጽሑፍ ቅርጽ ካስፈለገ ያንን መከተል አለበት።"
];

const POA_EXAMPLES = [
  "በባንክ የተወሰኑ ጉዳዮችን እንዲያከናውን።",
  "ከጉምሩክ ዕቃ እንዲያስለቅቅ።",
  "የንግድ ሰነዶችን እንዲፈርም።",
  "ንብረትን እንዲያስተዳድር።",
  "በፍርድ ቤት ወይም በመንግሥት ተቋም የተወሰነ ጉዳይ እንዲወክልዎ።"
];

const POA_TYPES = [
  ["ልዩ ውክልና", "አንድ የተወሰነ ሥራ ብቻ"],
  ["አጠቃላይ ውክልና", "በተገለጹ የጉዳይ ዘርፎች ሰፊ ሥልጣን"],
  ["የንግድ ውክልና", "የንግድ ሥራን በእርስዎ ስም ማከናወን"],
  ["የንብረት ውክልና", "የተወሰነ ንብረትን ማስተዳደር"]
];

const OFFICIAL_LINKS = [
  {
    name: "የፍትሕ ሚኒስቴር — የሰነዶች ማረጋገጥና ምዝገባ አዋጅ (ቁ. 334/1995)",
    url: "https://justice.gov.et/en/law/authentication-and-registration-of-documents-proclamation/",
    key: "moj-law"
  },
  {
    name: "የኢትዮጵያ ኤምባሲ — Power of Attorney አገልግሎት",
    url: "https://ethiopianembassy.org/power-of-attorney/",
    key: "embassy-poa"
  },
  {
    name: "የኤምባሲ ኮንሱላር አገልግሎት — የውክልና ሰነድ መስፈርቶች",
    url: "https://consular.ethiopianembassy.org/service-details_PoA",
    key: "consular-poa"
  },
  {
    name: "Digital MoFA — የውክልና ሰነድ ኦንላይን ማመልከቻ",
    url: "https://digitalmofa.com/",
    key: "digital-mofa"
  }
];

const EthiopianLawModal = ({ onClose }) => {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm no-print"
      onClick={onClose}
      data-testid="ethiopian-law-modal"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="trade-card ethiopian-law-shell rounded-sm p-6 w-full max-w-4xl max-h-[88vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-6 no-print">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-[#D4AF37]/10 rounded-sm flex items-center justify-center flex-shrink-0">
              <Scale className="w-5 h-5 trade-gold" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] trade-gold">የኢትዮጵያ ሕግ · Ethiopian Law</p>
              <h2 className="text-2xl font-serif font-bold" data-testid="ethiopian-law-title">ስምምነት፣ ውል እና የውክልና ሥልጣን</h2>
              <p className="text-xs text-white/45 mt-1">Agreements, contracts and Power of Attorney — in Amharic</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => window.print()}
              className="trade-btn-primary rounded-sm h-10 px-4 text-sm flex items-center gap-2"
              data-testid="law-print-btn"
            >
              <Printer size={15} /> Print
            </button>
            <button onClick={onClose} className="p-2 text-white/40 hover:text-white" data-testid="law-close">
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="ethiopian-law-print bg-white text-slate-900 rounded-sm p-6 md:p-8" data-testid="ethiopian-law-content">
          <div className="text-center mb-8">
            <p className="text-[10px] uppercase tracking-[0.3em] text-amber-700 mb-2">የኢትዮጵያ ሕግ · ትምህርት</p>
            <h1 className="text-3xl font-serif font-bold mb-2">ስምምነት፣ ውል እና የውክልና ሥልጣን</h1>
            <p className="text-sm text-slate-600">
              ከሰዎች ጋር በንግድ፣ በገንዘብ፣ በንብረት፣ በማስመጣትና በማስላክ ወይም በአጋርነት ሲሠሩ ራስዎን በሕግ መጠበቅ በጣም አስፈላጊ ነው።
            </p>
          </div>

          {/* 1. Contract law */}
          <div className="mb-8" data-testid="law-section-contract">
            <h2 className="font-serif font-bold text-xl mb-3">1. በኢትዮጵያ የውል ሕግ</h2>
            <p className="text-sm text-slate-700 mb-3">
              ዋናው ሕግ የኢትዮጵያ የፍትሐ ብሔር ሕግ (Civil Code Proclamation No. 165/1960) ነው። በተለይ አንቀጽ 1675–1678 የውል መሠረታዊ መስፈርቶችን ይደነግጋሉ።
            </p>
            <p className="text-sm font-semibold mb-2">አንቀጽ 1678 በተግባር ምን ማለት ነው? — ሕጋዊ ውል ለመኖሩ የሚከተሉት ያስፈልጋሉ፦</p>
            <ol className="space-y-2 mb-4">
              {CONTRACT_REQUIREMENTS.map((req, i) => (
                <li key={req} className="flex items-start gap-3 text-sm text-slate-700" data-testid={`law-contract-req-${i + 1}`}>
                  <span className="w-5 h-5 rounded-sm bg-amber-100 text-amber-800 flex items-center justify-center flex-shrink-0 text-[11px] font-bold mt-0.5">{i + 1}</span>
                  {req}
                </li>
              ))}
            </ol>
            <p className="text-sm text-slate-700 bg-amber-50 border-l-2 border-amber-600 pl-3 py-2">
              <span className="font-semibold">ለምሳሌ፦</span> ከአንድ ሰው ጋር 10 ሚሊዮን ብር የንግድ አጋርነት ካደረጉ፣ ማን ምን ያህል ገንዘብ እንደሚያዋጣ፣ ትርፍና ኪሳራ እንዴት እንደሚከፈል በጽሑፍ ማስቀመጥ አለብዎት።
            </p>
          </div>

          {/* 2. Ten rules */}
          <div className="mb-8" data-testid="law-section-rules">
            <h2 className="font-serif font-bold text-xl mb-3">2. ከሰዎች ጋር ስምምነት ሲያደርጉ የሚያስፈልጉ 10 ደንቦች</h2>
            <ol className="space-y-2 mb-4">
              {AGREEMENT_RULES.map((rule, i) => (
                <li key={rule} className="flex items-start gap-3 p-3 border border-slate-300 rounded-sm" data-testid={`law-rule-${i + 1}`}>
                  <span className="w-6 h-6 rounded-sm bg-amber-100 text-amber-800 flex items-center justify-center flex-shrink-0 text-xs font-bold">{i + 1}</span>
                  <span className="text-sm">{rule}</span>
                </li>
              ))}
            </ol>
            <p className="text-sm text-slate-700 bg-amber-50 border-l-2 border-amber-600 pl-3 py-2">
              <span className="font-semibold">አስፈላጊ:</span> የምስክር ፊርማ ብቻ ሁሉንም የሕግ መስፈርቶች አይተካም። የውሉ ዓይነት ምን ያህል ማረጋገጫ እንደሚፈልግ መፈተሽ አለበት።
            </p>
          </div>

          {/* 3. Power of Attorney */}
          <div className="mb-8" data-testid="law-section-poa">
            <h2 className="font-serif font-bold text-xl mb-3">3. የውክልና ሥልጣን (Power of Attorney)</h2>
            <p className="text-sm text-slate-700 mb-3">
              Power of Attorney ማለት እርስዎ በግል መገኘት ሳይችሉ ሌላ ሰው በስምዎ የተወሰኑ ሕጋዊ ተግባራትን እንዲያከናውን የሚሰጡት የውክልና ሥልጣን ነው።
            </p>
            <p className="text-sm font-semibold mb-2">ለምሳሌ፦</p>
            <ul className="list-disc pl-6 space-y-1 text-sm text-slate-700 mb-4">
              {POA_EXAMPLES.map(ex => <li key={ex}>{ex}</li>)}
            </ul>
            <p className="text-sm font-semibold mb-2">የውክልና ዓይነቶች</p>
            <div className="border border-slate-300 rounded-sm overflow-hidden mb-4">
              {POA_TYPES.map(([type, meaning], i) => (
                <div key={type} className={`grid grid-cols-2 text-sm ${i % 2 === 0 ? "bg-slate-50" : "bg-white"}`} data-testid={`law-poa-type-${i + 1}`}>
                  <p className="font-semibold p-3 border-r border-slate-300">{type}</p>
                  <p className="p-3 text-slate-700">{meaning}</p>
                </div>
              ))}
            </div>
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-sm p-3 mb-2" data-testid="law-poa-caution">
              <AlertTriangle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">
                <span className="font-semibold">የሕግ ጥንቃቄ:</span> የውክልና ሥልጣን በግልጽ የተጻፉ ወሰኖች ሊኖሩት ይገባል። ለሰው የባንክ ገንዘብዎን እንዲያወጣ፣ ንብረትዎን እንዲሸጥ ወይም ውል እንዲፈርም ሥልጣን ከሰጡት በተለይ የሚፈቀደውንና የማይፈቀደውን በግልጽ ይግለጹ።
              </p>
            </div>
          </div>

          {/* 4. POA template */}
          <div className="mb-8" data-testid="law-section-template">
            <h2 className="font-serif font-bold text-xl mb-1">4. የውክልና ሰነድ ናሙና</h2>
            <p className="text-xs text-slate-500 mb-4">ይህን እንደ መነሻ ናሙና መጠቀም ይችላሉ — በማያ ገጽ ላይ ይሙሉ እና ያትሙ።</p>
            <div className="border-2 border-slate-900 rounded-sm p-5 md:p-6" data-testid="poa-template">
              <p className="text-center font-serif font-bold text-lg">የውክልና ሥልጣን ሰነድ</p>
              <p className="text-center text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-5">Power of Attorney</p>

              <div className="space-y-4 text-sm mb-6">
                <label className="flex items-end gap-2"><span className="font-semibold whitespace-nowrap">የወካይ ሙሉ ስም፦</span><input className={blankLine} data-testid="poa-grantor-name" /></label>
                <label className="flex items-end gap-2"><span className="font-semibold whitespace-nowrap">የመታወቂያ ቁጥር፦</span><input className={blankLine} data-testid="poa-grantor-id" /></label>
                <label className="flex items-end gap-2"><span className="font-semibold whitespace-nowrap">አድራሻ፦</span><input className={blankLine} data-testid="poa-grantor-address" /></label>
                <label className="flex items-end gap-2"><span className="font-semibold whitespace-nowrap">የተወካይ ሙሉ ስም፦</span><input className={blankLine} data-testid="poa-agent-name" /></label>
                <label className="flex items-end gap-2"><span className="font-semibold whitespace-nowrap">የመታወቂያ ቁጥር፦</span><input className={blankLine} data-testid="poa-agent-id" /></label>
                <label className="flex items-end gap-2"><span className="font-semibold whitespace-nowrap">አድራሻ፦</span><input className={blankLine} data-testid="poa-agent-address" /></label>
              </div>

              <div className="space-y-4 text-sm mb-6">
                <div>
                  <p className="font-semibold mb-1">1. የውክልናው ዓላማ</p>
                  <p className="text-slate-700">እኔ ከዚህ በታች ፊርማዬን ያስቀመጥኩት ወካይ፣ ከላይ የተገለጸውን ተወካይ በእኔ ስም የሚከተሉትን ተግባራት እንዲፈጽም ወክዬዋለሁ።</p>
                </div>
                <div>
                  <p className="font-semibold mb-1">2. የተሰጠው ሥልጣን</p>
                  <ul className="list-disc pl-6 space-y-1 text-slate-700">
                    <li>በተወሰኑ የንግድ ጉዳዮች ላይ እንዲወክለኝ።</li>
                    <li>ከተመለከቱ መንግሥታዊ ተቋማት ጋር እንዲገናኝ።</li>
                    <li>የተፈቀዱ ሰነዶችን እንዲያቀርብና እንዲቀበል።</li>
                    <li>ከዚህ ውክልና ዓላማ ጋር የተያያዙ ሌሎች በግልጽ የተፈቀዱ ተግባራትን እንዲያከናውን።</li>
                  </ul>
                </div>
                <div>
                  <p className="font-semibold mb-1">3. የሥልጣን ወሰን</p>
                  <p className="text-slate-700">ተወካዩ ከላይ ከተገለጹት ሥልጣኖች ውጭ ያለኝን ንብረት መሸጥ፣ ገንዘብ መበደር፣ የባንክ ሂሳብ መዝጋት ወይም ለሌላ ሰው ውክልና መስጠት አይችልም፤ በተለየ የጽሑፍ ፈቃድ ካልተፈቀደለት በስተቀር።</p>
                </div>
                <div>
                  <p className="font-semibold mb-2">4. የውክልናው የጊዜ ገደብ</p>
                  <label className="flex items-end gap-2 mb-3"><span className="whitespace-nowrap">የሚጀምርበት ቀን፦</span><input className={blankLine} data-testid="poa-start-date" /></label>
                  <label className="flex items-end gap-2"><span className="whitespace-nowrap">የሚያበቃበት ቀን፦</span><input className={blankLine} data-testid="poa-end-date" /></label>
                </div>
                <div>
                  <p className="font-semibold mb-1">5. የማቋረጥ መብት</p>
                  <p className="text-slate-700">ወካዩ በሚመለከተው ሕግና ሥነ-ሥርዓት መሠረት ውክልናውን ማቋረጥ ይችላል።</p>
                </div>
                <div>
                  <p className="font-semibold mb-2">6. ፊርማ</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div data-testid="poa-signature-grantor">
                      <div className="border-b border-slate-500 h-8" />
                      <p className="text-[11px] text-slate-500 mt-1">የወካይ ስምና ፊርማ</p>
                    </div>
                    <div data-testid="poa-signature-agent">
                      <div className="border-b border-slate-500 h-8" />
                      <p className="text-[11px] text-slate-500 mt-1">የተወካይ ስምና ፊርማ</p>
                    </div>
                    <div>
                      <div className="border-b border-slate-500 h-8" />
                      <p className="text-[11px] text-slate-500 mt-1">ቀን</p>
                    </div>
                    <div>
                      <div className="border-b border-slate-500 h-8" />
                      <p className="text-[11px] text-slate-500 mt-1">የሰነድ ማረጋገጫ ባለሥልጣን</p>
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-[11px] text-slate-500 text-center border-t border-slate-300 pt-3">
                ይህ ናሙና ብቻ ነው፤ ለባንክ፣ ለመሬት፣ ለቤት ሽያጭ ወይም ለጉምሩክ የሚያገለግል የመጨረሻ ሕጋዊ ሰነድ ከመሆኑ በፊት በሚመለከተው ባለሥልጣን ማረጋገጥ ያስፈልጋል።
              </p>
            </div>
          </div>

          {/* 5. Official links */}
          <div className="mb-6 no-print" data-testid="law-section-links">
            <h2 className="font-serif font-bold text-xl mb-3">5. ኦፊሴላዊ የሕግ ድረ-ገጾች</h2>
            <div className="space-y-2">
              {OFFICIAL_LINKS.map(link => (
                <a
                  key={link.key}
                  href={link.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-between gap-3 p-3 border border-slate-300 rounded-sm hover:border-amber-600 transition-colors text-sm"
                  data-testid={`law-link-${link.key}`}
                >
                  <span>{link.name}</span>
                  <ExternalLink size={14} className="text-amber-700 flex-shrink-0" />
                </a>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-3">
              ማስታወሻ: የሰነዶች ማረጋገጥና ምዝገባ አዋጅ ቁጥር 334/1995 ነው (የኤጀንሲው ምስረታ ደንብ 379/2016)። አሜሪካ ውስጥ ሆነው ለኢትዮጵያ የውክልና ሰነድ መስጠት ከፈለጉ፣ አሁን በኦንላይን ብቻ (Digital MoFA) ይፈጸማል — የኤምባሲውን የማረጋገጫ መስፈርት ይመልከቱ።
            </p>
          </div>

          <p className="text-[10px] text-slate-500 text-center border-t border-slate-300 pt-3" data-testid="law-disclaimer">
            ይህ ትምህርት መረጃ ብቻ ነው፣ የሕግ ምክር አይደለም። ለውሳኔ ጉዳዮች የሕግ ባለሙያ ወይም የፍትሕ ሚኒስቴር ሰነዶች ማረጋገጥና ምዝገባ አገልግሎት ያማክሩ።
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default EthiopianLawModal;
